import { useEffect, useMemo, useRef, useState } from 'react';
import Swal from 'sweetalert2';
import { FaCheckCircle, FaRocket, FaTimes } from 'react-icons/fa';
import './SendAlertScrollPrompt.css';

const REOPEN_DELAY_MS = 10 * 60 * 1000;

const normalizeRole = (role) => String(role || '').trim().toLowerCase();

function SendAlertScrollPrompt({
  pageKey,
  pageLabel,
  jobPosition,
  user,
  navigate,
  delayMs = 40000,
  targetRoute = '/employer-send-alert',
  targetRoles = ['employer'],
  targetRoleLabel = 'employer',
  registerDefaultTab = 'employer',
  loginDefaultTab = 'employer',
  prefillJobPositionOnContinue = true
}) {
  const cooldownKey = useMemo(() => `send-alert-scroll-prompt:${pageKey}:cooldown`, [pageKey]);
  const timeoutRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (!pageKey || typeof window === 'undefined') return undefined;

    const cooldownUntil = Number(localStorage.getItem(cooldownKey) || 0);
    const now = Date.now();
    const waitMs = cooldownUntil > now ? cooldownUntil - now : delayMs;

    timeoutRef.current = window.setTimeout(() => {
      setIsVisible(true);
    }, waitMs);

    return () => {
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
      }
    };
  }, [cooldownKey, delayMs, pageKey]);

  const dismissForCooldown = () => {
    localStorage.setItem(cooldownKey, String(Date.now() + REOPEN_DELAY_MS));
    setIsVisible(false);
  };

  const handleContinue = async () => {
    setIsVisible(false);

    const role = normalizeRole(user?.role);
    const normalizedTargetRoles = targetRoles.map(normalizeRole);
    const hasRequiredRole = normalizedTargetRoles.includes(role);

    if (hasRequiredRole) {
      const params = new URLSearchParams({
        sourcePage: pageKey
      });
      if (prefillJobPositionOnContinue && jobPosition) {
        params.set('jobPosition', jobPosition);
      }
      navigate(params.toString() ? `${targetRoute}?${params.toString()}` : targetRoute);
      return;
    }

    if (!user?.id) {
      const result = await Swal.fire({
        title: '<div class="swal-custom-title">Please Register or Login</div>',
        text: `You must be registered and logged in as ${targetRoleLabel} to send alert${jobPosition ? ` for ${jobPosition}` : ''}`,
        icon: 'info',
        showCancelButton: true,
        showDenyButton: true,
        confirmButtonText: 'Register',
        cancelButtonText: 'Login',
        denyButtonText: '<i class="bi bi-x-circle"></i> Not Now',
        customClass: {
          popup: 'registration-popup',
          confirmButton: 'swal-register-btn',
          cancelButton: 'swal-login-btn',
          denyButton: 'swal-whatsapp-btn',
          actions: 'swal-actions-column'
        },
        buttonsStyling: false,
        showCloseButton: true,
        footer: '<div class="swal-footer-text">Registration helps prevent fake profiles</div>'
      });

      if (result.isConfirmed) {
        navigate('/register', { state: { defaultTab: registerDefaultTab } });
        return;
      }

      if (result.dismiss === Swal.DismissReason.cancel) {
        navigate('/login', { state: { defaultTab: loginDefaultTab } });
        return;
      }

      dismissForCooldown();
      return;
    }

    await Swal.fire({
      icon: 'info',
      title: `${targetRoleLabel.charAt(0).toUpperCase()}${targetRoleLabel.slice(1)} Login Required`,
      text: `Please login with a ${targetRoleLabel} account to continue.`,
      confirmButtonText: 'Login'
    });

    navigate('/login', { state: { defaultTab: loginDefaultTab } });
  };

  if (!isVisible) return null;

  return (
    <div className="send-alert-theme-overlay" role="presentation">
      <div className="send-alert-theme-modal" role="dialog" aria-modal="true">
        <button
          type="button"
          className="send-alert-theme-close"
          onClick={dismissForCooldown}
          aria-label="Close send alert prompt"
        >
          <FaTimes />
        </button>

        <div className="send-alert-theme-header">
          <div className="send-alert-theme-header-icon">
            <FaRocket />
          </div>
          <div className="send-alert-theme-header-text">
            Stop messaging {jobPosition.toLowerCase()}s one by one
          </div>
        </div>

        <div className="send-alert-theme-body">
          <h3 className="send-alert-theme-title">
            Tell us what you need — we'll send it to every matching {jobPosition.toLowerCase()} instantly.
          </h3>

          <div className="send-alert-theme-benefits">
            <div className="send-alert-theme-benefit">
              <FaCheckCircle className="send-alert-theme-benefit-icon" />
              <span>Reaches all matching {jobPosition.toLowerCase()}s</span>
            </div>
            <div className="send-alert-theme-benefit">
              <FaCheckCircle className="send-alert-theme-benefit-icon" />
              <span>Interested {jobPosition.toLowerCase()}s reply directly to you</span>
            </div>
            <div className="send-alert-theme-benefit">
              <FaCheckCircle className="send-alert-theme-benefit-icon" />
              <span>Save hours of searching and one-by-one messaging</span>
            </div>
            <div className="send-alert-theme-benefit">
              <FaCheckCircle className="send-alert-theme-benefit-icon" />
              <span>Track all replies in one place</span>
            </div>
          </div>

          <div className="send-alert-theme-highlight send-alert-theme-highlight-standalone">
            <span className="send-alert-theme-highlight-bolt">⚡</span>
            <span>
              Send bulk message to <strong>{jobPosition.toLowerCase()}s instantly</strong>
            </span>
          </div>

          <button
            type="button"
            className="send-alert-theme-continue"
            onClick={handleContinue}
          >
            Send My Requirement
          </button>

          <div className="send-alert-theme-footer">
            Free • takes about 1 minute
          </div>
        </div>
      </div>
    </div>
  );
}

export default SendAlertScrollPrompt;
